import {
  SLA_DEFAULT_WARN_AT_PERCENT,
  SlaDisplayStatus,
  SlaEscalationLevel,
} from '../types/ticket-sla'

// =====================
// Ticket SLA display status
// =====================
//
// Shared by the web SLA chip / table cell (live-ticking client side) and any
// server-side summaries. Picks the most urgent applicable timer — the
// first-response timer while it's unmet, the resolution timer after — and
// classifies it. Escalation levels stamped by the SLA cron take precedence
// over local time math so the chip and the notifications never disagree, but
// the time math keeps the chip honest between cron ticks.

/** The subset of Ticket fields the status computation reads. */
export interface TicketSlaFields {
  createdAt?: Date | string | null
  resolvedAt?: Date | string | null
  slaDueAt?: Date | string | null
  slaFirstResponseDueAt?: Date | string | null
  firstRespondedAt?: Date | string | null
  slaPausedAt?: Date | string | null
  slaResolutionEscalationLevel?: number | null
  slaFirstResponseEscalationLevel?: number | null
}

export interface TicketSlaStatusResult {
  status: SlaDisplayStatus
  /** Which timer the status describes. */
  timer: 'FIRST_RESPONSE' | 'RESOLUTION'
  dueAt: Date
  /** Positive = time left, negative = overdue. Null for PAUSED/MET. */
  msRemaining: number | null
}

function toDate(value: Date | string | null | undefined): Date | null {
  if (!value) return null
  const date = value instanceof Date ? value : new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

function classifyOpenTimer(
  createdAt: Date,
  dueAt: Date,
  escalationLevel: number,
  now: Date,
  warnAtPercent: number,
): SlaDisplayStatus {
  if (
    escalationLevel >= SlaEscalationLevel.BREACHED ||
    now.getTime() >= dueAt.getTime()
  ) {
    return SlaDisplayStatus.BREACHED
  }
  const window = dueAt.getTime() - createdAt.getTime()
  const warnAt = dueAt.getTime() - ((100 - warnAtPercent) / 100) * window
  if (
    escalationLevel >= SlaEscalationLevel.WARNED ||
    (window > 0 && now.getTime() >= warnAt)
  ) {
    return SlaDisplayStatus.AT_RISK
  }
  return SlaDisplayStatus.ON_TRACK
}

/**
 * Compute the SLA chip status for a ticket, or null when no SLA timer applies.
 * `warnAtPercent` mirrors the type's slaConfig; callers without access to the
 * type config use the platform default (the cron-stamped escalation levels
 * still win, so a custom warn threshold surfaces on the next cron pass).
 */
export function computeTicketSlaStatus(
  ticket: TicketSlaFields,
  now: Date = new Date(),
  warnAtPercent: number = SLA_DEFAULT_WARN_AT_PERCENT,
): TicketSlaStatusResult | null {
  const createdAt = toDate(ticket.createdAt) ?? now
  const resolvedAt = toDate(ticket.resolvedAt)
  const slaDueAt = toDate(ticket.slaDueAt)
  const firstResponseDueAt = toDate(ticket.slaFirstResponseDueAt)
  const firstRespondedAt = toDate(ticket.firstRespondedAt)
  const pausedAt = toDate(ticket.slaPausedAt)

  // Resolved: timers are stopped — report MET/BREACHED for the resolution
  // target (falling back to the first-response timer when that's the only
  // one configured).
  if (resolvedAt) {
    if (slaDueAt) {
      return {
        status:
          resolvedAt.getTime() <= slaDueAt.getTime()
            ? SlaDisplayStatus.MET
            : SlaDisplayStatus.BREACHED,
        timer: 'RESOLUTION',
        dueAt: slaDueAt,
        msRemaining: null,
      }
    }
    if (firstResponseDueAt) {
      const respondedBy = firstRespondedAt ?? resolvedAt
      return {
        status:
          respondedBy.getTime() <= firstResponseDueAt.getTime()
            ? SlaDisplayStatus.MET
            : SlaDisplayStatus.BREACHED,
        timer: 'FIRST_RESPONSE',
        dueAt: firstResponseDueAt,
        msRemaining: null,
      }
    }
    return null
  }

  // Open: first-response timer while unmet, resolution timer otherwise.
  const useFirstResponse = Boolean(firstResponseDueAt && !firstRespondedAt)
  const dueAt = useFirstResponse ? firstResponseDueAt : slaDueAt
  if (!dueAt) return null

  if (pausedAt) {
    return {
      status: SlaDisplayStatus.PAUSED,
      timer: useFirstResponse ? 'FIRST_RESPONSE' : 'RESOLUTION',
      dueAt,
      msRemaining: null,
    }
  }

  const escalationLevel = useFirstResponse
    ? (ticket.slaFirstResponseEscalationLevel ?? 0)
    : (ticket.slaResolutionEscalationLevel ?? 0)
  return {
    status: classifyOpenTimer(createdAt, dueAt, escalationLevel, now, warnAtPercent),
    timer: useFirstResponse ? 'FIRST_RESPONSE' : 'RESOLUTION',
    dueAt,
    msRemaining: dueAt.getTime() - now.getTime(),
  }
}
